import { memo, useEffect, useRef, useState } from 'react';
import styles from './ControlsPanel.module.css';
import SegmentedToggle from './SegmentedToggle';
import MultipleDropdown from './MultipleDropdown';
import { MAIN_CHART_METRIC_OPTIONS } from '../config/metricsConfig';

const VALUE_BASIS_DEFAULT = 'marketCap';
const DISPLAY_SCALE_DEFAULT = 'perShare';
const AXIS_SCALE_DEFAULT = 'linear';

const VALUE_BASIS_OPTIONS = [
  { value: 'marketCap', label: 'Market Cap' },
  { value: 'enterpriseValue', label: 'Enterprise Value' },
];

const DISPLAY_SCALE_OPTIONS = [
  { value: 'perShare', label: 'Per Share' },
  { value: 'total', label: 'Total' },
];

const AXIS_SCALE_OPTIONS = [
  { value: 'linear', label: 'Linear' },
  { value: 'logarithmic', label: 'Log' },
];

// Boolean, but given the same two-pill shape as the other three toggles
// (caption + SegmentedToggle) so it doesn't read as a different control
// type in the menu. SegmentedToggle compares option.value with === , so a
// plain boolean works as the option value just like the string enums above.
const SBC_ADJUSTMENT_OPTIONS = [
  { value: false, label: 'Reported' },
  { value: true, label: 'After SBC' },
];

const ControlsPanel = ({
  metric,
  multipleMode,
  multipleValue,
  multipleDefault,
  valueBasis = VALUE_BASIS_DEFAULT,
  displayScale = DISPLAY_SCALE_DEFAULT,
  axisScale = AXIS_SCALE_DEFAULT,
  adjustForSbc = false,
  onMetricChange,
  onMultipleCommit,
  onValueBasisChange = () => {},
  onDisplayScaleChange = () => {},
  onAxisScaleChange = () => {},
  onAdjustForSbcChange = () => {},
}) => {
  // Value basis / Scale / Axis / Adjust for SBC used to sit inline as a
  // wrapping row of toggles — pushed below the fold on narrow layouts and
  // competing with the metric/multiple inputs for attention even though
  // they're secondary display options, not primary inputs. They now live in
  // a gear menu (same open/outside-click/Escape pattern as
  // MetricMultiSelect's checkbox dropdown), so the panel's default state
  // only shows what most users need: metric, multiple, and the chart. The
  // dot on the gear flags when a hidden setting differs from its default,
  // so "why does this look different" is still answerable without opening
  // the menu.
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const settingsRef = useRef(null);

  useEffect(() => {
    if (!isSettingsOpen) return;
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setIsSettingsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isSettingsOpen]);

  const hasNonDefaultSettings = valueBasis !== VALUE_BASIS_DEFAULT
    || displayScale !== DISPLAY_SCALE_DEFAULT
    || axisScale !== AXIS_SCALE_DEFAULT
    || adjustForSbc;

  return (
    // Every control here is now live: the metric and multiple dropdowns rebuild
    // the chart client-side (no refetch) like the settings-menu toggles, so
    // they all live together in one "view options" panel — no <form>/Go
    // gating (the only fetch trigger left is the ticker box's Go button in
    // SymbolSearch). The metric + multiple row sits first since it drives the
    // primary fair-value line; the gear menu refines how it's drawn.
    <div className={styles.liveControls}>
      <div className={styles.topRow}>
        <div className={styles.inputGroup}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Metric</span>
            <select className={styles.metricDropdown} value={metric} onChange={onMetricChange} aria-label="Comparison metric">
              {MAIN_CHART_METRIC_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          {/* Multiplication sign between the two dropdowns so the pair reads as
              "metric × multiple" (e.g. Earnings × 15) — the fair-value line is the
              metric scaled by this multiple. aria-hidden: it's decorative framing,
              the two controls already carry their own labels. */}
          <span className={styles.timesSign} aria-hidden="true">×</span>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Multiple</span>
            <MultipleDropdown
              mode={multipleMode}
              value={multipleValue}
              defaultValue={multipleDefault}
              onCommit={onMultipleCommit}
            />
          </div>
        </div>

        <div
          className={styles.settingsMenu}
          ref={settingsRef}
          onKeyDown={(event) => { if (event.key === 'Escape') setIsSettingsOpen(false); }}
        >
          <button
            type="button"
            className={styles.settingsToggle}
            aria-haspopup="true"
            aria-expanded={isSettingsOpen}
            aria-label="Chart display settings"
            title="Chart display settings"
            onClick={() => setIsSettingsOpen((prev) => !prev)}
          >
            <GearIcon />
            {hasNonDefaultSettings && <span className={styles.settingsDot} aria-hidden="true" />}
          </button>

          {isSettingsOpen && (
            <div className={styles.settingsPanel}>
              <div className={styles.toggleField}>
                <span className={styles.fieldLabel}>Value basis</span>
                <SegmentedToggle
                  options={VALUE_BASIS_OPTIONS}
                  value={valueBasis}
                  onChange={onValueBasisChange}
                  ariaLabel="Company value basis"
                />
              </div>
              <div className={styles.toggleField}>
                <span className={styles.fieldLabel}>Scale</span>
                <SegmentedToggle
                  options={DISPLAY_SCALE_OPTIONS}
                  value={displayScale}
                  onChange={onDisplayScaleChange}
                  ariaLabel="Display scale"
                />
              </div>
              <div className={styles.toggleField}>
                {/* A SegmentedToggle like its neighbors, even though the
                    underlying state is one boolean, not two named modes —
                    visual consistency with Value basis/Scale/Axis wins here.
                    onAdjustForSbcChange's contract is still an event (it was
                    a plain checkbox's onChange), so wrap the raw boolean
                    SegmentedToggle hands back into a fake one rather than
                    touching every caller. Affects FCF/OCF display only
                    (useStockData.js's applySbcAdjustment); EPS, EBITDA, net
                    income, revenue and dividends are unaffected, and the
                    Quality score always uses after-SBC FCF regardless of this
                    switch (SCORE_CARDS.md §1.6). */}
                <span className={styles.fieldLabel}>FCF/OCF</span>
                <SegmentedToggle
                  options={SBC_ADJUSTMENT_OPTIONS}
                  value={adjustForSbc}
                  onChange={(checked) => onAdjustForSbcChange({ target: { checked } })}
                  ariaLabel="FCF/OCF SBC adjustment"
                />
              </div>
              <div className={styles.toggleField}>
                <span className={styles.fieldLabel}>Axis</span>
                <SegmentedToggle
                  options={AXIS_SCALE_OPTIONS}
                  value={axisScale}
                  onChange={onAxisScaleChange}
                  ariaLabel="Price axis scale"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Feather-style "settings" cog — stroke-only to match the chevron already
// drawn into .metricDropdown's background-image, so every icon in this
// panel reads as the same visual family.
const GearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82A1.65 1.65 0 0 0 3 13.09H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export default memo(ControlsPanel);
